import React from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { SparkAccordion, SparkCircularProgress } from 'sharedComponents/UI';
import type { RecommendedMemories } from 'types/Workspace';
import { useSparkRunRecommendations } from 'hooks/spark/useSparkRunRecommendations';
import { useSparkRunInputParameterRecommendations } from 'hooks/spark/useSparkRunInputParameterRecommendations';
import { useSparkRunsRecommendationsQueueJob } from 'hooks/SparkRuns';
import type { SparkRunInputParameterRecommendation } from 'types/Spark/SparkRunInputParameterRecommendations';
import KnowledgeTab from 'components/Settings/workspace/SingleWorkSpace/KnowledgeTab';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDialog } from 'hooks/common';

type ApplyKnowledgeProps = {
  recommendedMemories: RecommendedMemories | undefined;
  workspaceID: string;
  sparkID: string;
  applyKnowledge: boolean;
  onApplyKnowledge: (
    Recommendations: SparkRunInputParameterRecommendation[]
  ) => void;
  setApplyKnowledge: React.Dispatch<React.SetStateAction<boolean>>;
  setIsApplyingKnowledge: React.Dispatch<React.SetStateAction<boolean>>;
};

const ApplyKnowledge = ({
  recommendedMemories,
  workspaceID,
  sparkID,
  applyKnowledge,
  onApplyKnowledge,
  setApplyKnowledge,
  setIsApplyingKnowledge,
}: ApplyKnowledgeProps) => {
  const { open, closeDialog } = useDialog(false);
  const { refetch: postSparkRunRecommendation, data } =
    useSparkRunRecommendations(sparkID, workspaceID);

  const {
    percentageComplete,
    isRunning,
    isComplete,
    setIsRunning,
    removeQuery,
  } = useSparkRunsRecommendationsQueueJob(data?.data.id);

  React.useEffect(() => {
    setIsApplyingKnowledge(isRunning);
  }, [isRunning, setIsApplyingKnowledge]);

  const { sparkRunInputParameterRecommendations } =
    useSparkRunInputParameterRecommendations(data?.data.id, isComplete);

  React.useEffect(() => {
    if (!!sparkRunInputParameterRecommendations) {
      onApplyKnowledge(sparkRunInputParameterRecommendations);
    }
  }, [onApplyKnowledge, sparkRunInputParameterRecommendations]);

  React.useEffect(() => {
    if (isRunning) {
      closeDialog();
    }
  }, [closeDialog, isRunning]);

  const handleApplyKnowLedge = async (checked: boolean) => {
    setApplyKnowledge(checked);
    if (!checked && !!data) {
      return;
    }
    await removeQuery();
    setIsRunning(true);
    const response = await postSparkRunRecommendation();
    const sparkRunRecommendationID = response.data?.data.id;
    if (!sparkRunRecommendationID) {
      return;
    }
  };

  const renderTitle = () => {
    return (
      <Grid
        container
        item
        alignItems='center'
        gap={2}
        wrap='nowrap'
        justifyContent='space-between'
      >
        <Box display='flex' gap={1} alignItems='center'>
          {!!recommendedMemories && (
            <SparkCircularProgress
              value={recommendedMemories?.percentageComplete}
              color={recommendedMemories?.percentageCompleteColour}
            />
          )}
          <FormControlLabel
            control={<Switch defaultChecked={false} />}
            label=''
            onClick={(e) => e.stopPropagation()}
            onChange={(e, checked) => {
              e.stopPropagation();
              e.preventDefault();
              handleApplyKnowLedge(checked);
            }}
            value={applyKnowledge}
            disabled={isRunning}
            sx={{
              '&.MuiFormControlLabel-root': {
                marginRight: 0,
                marginBottom: 0.5,
              },
            }}
          />
          <Typography>Apply knowledge</Typography>
          <Tooltip
            title='Pre-fill the form using Workspace knowledge'
            placement='top'
            arrow
            enterTouchDelay={0}
            sx={{ color: (theme) => theme.palette.labelColor }}
          >
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        </Box>
        {isRunning && (
          <Box width='30%' justifySelf='flex-end'>
            <LinearProgress
              sx={{ height: '8px', borderRadius: '10px' }}
              variant='determinate'
              value={percentageComplete}
            />
          </Box>
        )}
      </Grid>
    );
  };

  return (
    <SparkAccordion
      title={renderTitle()}
      sx={{
        bgcolor: 'white',
        border: (theme) => `1px solid ${theme.palette.borderColor}`,
        marginTop: 2,
        width: 'unset',
      }}
      titleContainerProps={{ width: '100%' }}
      summaryStyles={{ padding: 1, cursor: 'pointer' }}
      detailsStyle={{ paddingX: 1, paddingBottom: 7, width: 'unset' }}
      initialExpand={open}
      showArrow={!isRunning}
      collapseProps={{ sx: { '& .MuiCollapse-wrapperInner': { zIndex: 0 } } }}
    >
      <KnowledgeTab workspaceID={workspaceID} isParentWorkspace={false} />
    </SparkAccordion>
  );
};

export default ApplyKnowledge;
